import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import { StandardAssignmentPins, StandardAssignmentPin } from '../../../standards-management/standard-assignment-pins';
import { HttpResponse } from '@angular/common/http';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {TreeGroupPosService} from '../tree-group-pos.service';
import {AdminService} from '../../../../../services/admin.service';
import {AnswerGroupPos} from '../answer-group-pos.model';
import {StepsControllerService} from '../../../../../services/steps-controller.service';
import {Router} from '@angular/router';
import { UserService } from '../../../../../services/user.service';
import { User } from '../../../../../models/user.model';

const HEAD_ROLE = 'head';
const CREATE_STANDARD_ROUTE = 'create-operating-standard';
const FIRST_STEP = 1;
const SECOND_STEP = 2;
const FIRST_GROUP = 0;
const HTTP_OK = 200;
const HTTP_NO_CONTENT = 204;
const EMPTY_GROUP_COUNT = 0;
const NOT_SELECTED = -1;
const REMOVED_SELECTION_COUNT = 1;

interface StandardGroupChoice {
  id: string;
  name?: string;
  positions: { id: string }[];
  onlyHeadConfirm?: boolean;
}

interface StandardAssignmentSource {
  group: { id: string; name?: string };
  positions: { id: string }[];
  onlyHeadConfirm?: boolean;
}

@Component({
  selector: 'app-tree-group-pos-block',
  templateUrl: './tree-group-pos-block.component.html',
  styleUrls: ['./tree-group-pos-block.component.css']
})
export class TreeGroupPosBlockComponent implements OnInit, OnDestroy {
  trackPin(index: number, row: StandardAssignmentPin): string { return row.scope.scopeId; }

  get undisplayedPins(): StandardAssignmentPin[] {
    if (!this.assignmentPins) { return []; }
    return this.assignmentPins.departmentRows.filter(row =>
      !this.allGroupsAndPositions.some(group => this.assignmentPins.rows[group.id] === row));
  }
  @Input() role: string = "";
  @Input() assignmentPins: StandardAssignmentPins;
  @Input() readOnly = false;
  @Input() publishedAssignments: StandardAssignmentSource[];
  public isLoaded = false;
  public errorMsg = false;
  private foundGroup = false;
  private foundGroupExecute = false;
  private foundGroupExecuteOnlyConfirm = false;
  private foundGroupOnlyConfirm = false;
  private foundPosExecute = false;
  user: User;
  // Save here all Groups and Positions
  public allGroupsAndPositions: StandardGroupChoice[] = [];
  // Save here opened Groups
  private selectedGroups: StandardGroupChoice[] = [];
  // This array set to service and send to server
  private readeForServer: AnswerGroupPos[] = [];
  private readeForServerOnlyConfirm: AnswerGroupPos[] = [];

  // Use for show/hide Head/Deputy only confirms checkboxes
  public showOnlyConfirmCheckBox = false;
  private destroy$ = new Subject<void>();

  constructor(private treeGroupPosService: TreeGroupPosService,
              private adminService: AdminService,
              private router: Router,
              private stepsControllerService: StepsControllerService,
              private userService: UserService) {
              // this.user = this.userService.getLocal();
              // if(this.role === 'ROLE_HEAD'){
              //   this.role = 'head';
              // }
  }

  ngOnInit(): void {
    this.reloadStoredSelections();
  }

  reloadStoredSelections(): void {
    this.isLoaded = false;
    this.errorMsg = false;
    this.readeForServer = [];
    this.readeForServerOnlyConfirm = [];
    this.selectedGroups = [];
    if (this.readOnly) { this.loadPublishedAssignments(); return; }
    this.treeGroupPosService.clearPositionsForServerLists();
    this.showOnlyConfirmCheckBox = this.router.url.includes(CREATE_STANDARD_ROUTE)
      && (this.stepsControllerService.activeStep === FIRST_STEP || this.stepsControllerService.activeStep === SECOND_STEP);
    this.initializeStoredSelections();
    this.loadAvailableGroups();
  }

  private loadPublishedAssignments(): void {
    const assignments = (this.publishedAssignments || []).filter(item => !!item.group && !!item.group.id);
    assignments.forEach(item => this.addStoredAssignment(item));
    this.allGroupsAndPositions = assignments.map(item => ({ id: item.group.id, name: item.group.name,
      positions: (item.positions || []).slice(), onlyHeadConfirm: item.onlyHeadConfirm === true }));
    this.selectedGroups = this.allGroupsAndPositions.slice();
    this.isLoaded = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeStoredSelections(): void {
    if (!this.stepsControllerService.hasCurrentStandard()) { return; }
    const standard = this.stepsControllerService.getCurrentStandard() as { groupsWithPositions?: StandardAssignmentSource[] };
    (standard.groupsWithPositions || []).forEach(item => this.addStoredAssignment(item));
    this.treeGroupPosService.setReadeForServer(this.readeForServer);
    this.treeGroupPosService.setReadeForServerOnlyConfirm(this.readeForServerOnlyConfirm);
  }

  private addStoredAssignment(item: StandardAssignmentSource): void {
    const selected = new AnswerGroupPos(item.group.id, item.onlyHeadConfirm === true);
    (item.positions || []).forEach(position => selected.addPos({ id: position.id }));
    if (selected.onlyHeadConfirm) {
      this.readeForServerOnlyConfirm.push(selected);
      return;
    }
    this.readeForServer.push(selected);
  }

  private loadAvailableGroups(): void {
    this.isLoaded = false;
    this.errorMsg = false;
    this.treeGroupPosService.setOperationTargetsReady(false);
    const request = this.role === HEAD_ROLE ? this.adminService.getGroupsAndPositionsHead() : this.adminService.getGroupsAndPositions();
    request.takeUntil(this.destroy$).subscribe(response => this.applyAvailableGroups(response), () => this.failAvailableGroups());
  }

  retryAvailableGroups(): void {
    if (this.readOnly) { return; }
    if (this.isLoaded && this.errorMsg) {
      this.loadAvailableGroups();
    }
  }

  private applyAvailableGroups(response: HttpResponse<Object>): void {
    const groups = response.status === HTTP_NO_CONTENT ? [] : response.body;
    if ((response.status !== HTTP_OK && response.status !== HTTP_NO_CONTENT) || !Array.isArray(groups)) {
      this.failAvailableGroups();
      return;
    }
    this.allGroupsAndPositions = (groups as StandardGroupChoice[]).map(group => Object.assign({}, group, { positions: group.positions || [] }));
    this.treeGroupPosService.setOperationTargetsReady(this.allGroupsAndPositions.length > EMPTY_GROUP_COUNT);
    if (this.role === HEAD_ROLE && !this.stepsControllerService.hasCurrentStandard() && this.allGroupsAndPositions.length) {
      this.fieldsChangeGroupInit(true, this.allGroupsAndPositions[FIRST_GROUP]);
    }
    this.selectedGroups = this.allGroupsAndPositions.filter(group => this.readeForServer.some(selected => selected.id === group.id)
      || this.readeForServerOnlyConfirm.some(selected => selected.id === group.id));
    this.errorMsg = false;
    this.isLoaded = true;
  }

  private failAvailableGroups(): void {
    this.allGroupsAndPositions = [];
    this.errorMsg = true;
    this.isLoaded = true;
  }

  // Check if group is opened
  isSelectedGroup(group) {
    this.foundGroup = false;
    if (this.selectedGroups.length > 0) {
      for (const grp of this.selectedGroups) {
        if (grp.id === group.id) {
          this.foundGroup = true;
          break;
        }
      }
    }
    return this.foundGroup;
  }

  // Open/close groups positions list
  selectGroupItem(group) {
    // If group is opened
    if (this.isSelectedGroup(group)) {
      // If group is unchecked
      if (!this.isSelectedGroupExecute(group)) {
        const index = this.selectedGroups.findIndex(x => x.id === group.id);
        if (index !== -1) {
          this.selectedGroups.splice(index, 1);
        }
      }
    } else {
      this.selectedGroups.push(group);
    }
  }

  // Check if group checkbox is checked
  isSelectedGroupExecute(group: StandardGroupChoice): boolean {
    return this.readeForServer.some(selected => selected.id === group.id && !selected.onlyHeadConfirm);
  }

  // Check if group checkbox is checked
  isSelectedGroupExecuteOne(group) {
    this.foundGroupExecute = false;
    if (this.readeForServer.length > 0) {
      for (const grp of this.readeForServer) {
        if (grp.id === group.id) {
          this.foundGroupExecute = true;
          break;
        }
      }
    }
    return this.foundGroupExecute;
  }

  // Check if group checkbox is checked for confirm
  isSelectedGroupOnlyConfirm(group) {
    this.foundGroupExecuteOnlyConfirm = false;
    if (this.readeForServerOnlyConfirm.length > 0) {
      for (const grp of this.readeForServerOnlyConfirm) {
        if (grp.id === group.id) {
          this.foundGroupExecuteOnlyConfirm = true;
          break;
        }
      }
    }
    return this.foundGroupExecuteOnlyConfirm;
  }

  // Checkbox actions on/off group
  fieldsChangeGroup(values: { currentTarget: { checked: boolean } }, group: StandardGroupChoice): void {
    if (this.readOnly) { return; }
    this.updateGroupSelection(values.currentTarget.checked, group, false);
  }

  fieldsChangeGroupInit(checked: boolean, group: StandardGroupChoice): void {
    if (this.readOnly) { return; }
    this.updateGroupSelection(checked, group, false);
  }

  // Checkbox actions on/off group
  fieldsChangeGroupOnlyConfirm(values: { currentTarget: { checked: boolean } }, group: StandardGroupChoice): void {
    if (this.readOnly) { return; }
    group.onlyHeadConfirm = values.currentTarget.checked;
    this.updateGroupSelection(values.currentTarget.checked, group, true);
  }

  private updateGroupSelection(checked: boolean, group: StandardGroupChoice, onlyConfirm: boolean): void {
    const selections = onlyConfirm ? this.readeForServerOnlyConfirm : this.readeForServer;
    const index = selections.findIndex(selected => selected.id === group.id);
    if (checked && index === NOT_SELECTED) {
      const selected = new AnswerGroupPos(group.id, group.onlyHeadConfirm);
      group.positions.forEach(position => selected.addPos({id: position.id}));
      selections.push(selected);
    }
    if (!checked && index !== NOT_SELECTED) { selections.splice(index, REMOVED_SELECTION_COUNT); }
    if (onlyConfirm) { this.treeGroupPosService.setReadeForServerOnlyConfirm(selections); }
    else { this.treeGroupPosService.setReadeForServer(selections); }
    if (checked && !this.isSelectedGroup(group)) { this.selectGroupItem(group); }
  }

  // Check if position checkbox is checked
  isSelectedPosExecute(pos, group) {
    this.foundPosExecute = false;
    if (this.readeForServer.length > 0) {
      // Get all positions of this group and check this pos
      const index = this.readeForServer.findIndex(x => x.id === group.id);
      if (index !== -1) {
        for (const ps of this.readeForServer[index].positions) {
          if (ps.id === pos.id) {
            this.foundPosExecute = true;
            break;
          }
        }
      }
    }
    return this.foundPosExecute;
  }

  // Checkbox actions on/off position
  fieldsChangePos(values: { currentTarget: { checked: boolean } }, position: { id: string }, group: StandardGroupChoice): void {
    if (this.readOnly) { return; }
    let selected = this.readeForServer.find(item => item.id === group.id);
    if (values.currentTarget.checked) {
      if (!selected) {
        selected = new AnswerGroupPos(group.id, group.onlyHeadConfirm);
        this.readeForServer.push(selected);
      }
      if (!selected.positions.some(item => item.id === position.id)) { selected.addPos({id: position.id}); }
    } else if (selected) {
      selected.positions = selected.positions.filter(item => item.id !== position.id);
      if (!selected.positions.length) { this.readeForServer = this.readeForServer.filter(item => item.id !== group.id); }
    }
    this.treeGroupPosService.setReadeForServer(this.readeForServer);
  }

}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/admin/create-operating-standard/tree-group-pos-service/tree-group-pos-block/tree-group-pos-block.component.ts